import {
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
  createContext,
  useContext,
} from "react";
import { Tab } from "@headlessui/react";
import {
  Input,
  Checkbox,
  Calendar,
  DateInput,
  useDateInput,
  NoMatchingResultIllustration,
  SearchIllustration,
  cn,
  Slot,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  Button,
  useCombobox,
} from "ui";
import { format, isSameYear, parse, subDays, subMonths } from "date-fns";
import { backendKeyMap } from "../utils/backendKeyMap";
import { snakeCaseToHumanReadable } from "../utils/snakeCaseToHumanReadable";
import { arrify } from "../utils/arrify";
import { axiosApiInstance } from "../pages/api/axios";
import {
  BadgeCheckIcon,
  CalendarIcon,
  ChipIcon,
  ClockIcon,
  CloudIcon,
  CodeCircleIcon,
  DocumentCodeIcon,
  DocumentMetaIcon,
  DollarCircleIcon,
  DollarSquareIcon,
  EmptyWalletTickIcon,
  FilterIcon,
  GlobeAltIcon,
  HierarchySquare2Icon,
  LibraryIcon,
  MoneyReciveIcon,
  MoneySendIcon,
  OptimizationIcon,
  PaymentProviderIcon,
  PercentageSquareIcon,
  RefreshIcon,
  SearchIcon,
  SecurityCardIcon,
  StatusOnlineIcon,
  TransactionsIcon,
  UserCircleIcon,
  UsersIcon,
  WalletIcon,
  XCircleIcon,
  XIcon,
  PayInProviderIcon,
  PhoneIcon,
  MailIcon,
  DocumentTextIcon,
  GlobeIcon,
  CardLookupIcon,
  CardEditIcon,
  ProviderLookupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentReportIcon,
  InformationCircleIcon,
  ErrorIcon,
} from "icons/outline";
import { useRouter } from "next/router";
import { useMoneyHashQuery } from "../hooks/useMoneyHashQuery";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useLocalStorage } from "react-use";
import useUpdateEffect from "../hooks/useUpdateEffect";
import { isValidDate } from "../utils/isValidDate";
import { GlobalSearch } from "./GlobalSearch";
import { useFeatureFlags } from "../hooks/query";

const ChoicesList = ({
  choices,
  isMultiple,
  isSelected,
  render,
  getMenuProps,
  getItemProps,
  highlightedIndex,
  showOptions,
  children,
  recentOptions,
}) => {
  return (
    <ul
      className="flex-1  overflow-y-scroll px-2"
      tabIndex={-1}
      {...getMenuProps?.()}
    >
      {recentOptions?.length > 0 && (
        <>
          <li className="px-2 pb-2 pt-3 text-[11px] font-medium uppercase text-foundations-bold">
            Recents
          </li>
          {recentOptions.map((item, index) => {
            const [value, name] = item;
            const isHighlighted = highlightedIndex === index;

            return (
              <li
                className={cn(
                  "flex cursor-pointer items-center justify-between rounded px-2 py-2 text-sm text-foundations-bolder",
                  isHighlighted && "bg-foundations-subtlest"
                )}
                key={value}
                {...getItemProps?.({ item, index })}
              >
                {render(name)}
              </li>
            );
          })}
          <li
            role="presentation"
            aria-hidden
            className="-mx-2 border-b border-foundations-subtle pb-1"
          />
        </>
      )}

      {showOptions && recentOptions && (
        <li className="px-2 pb-2 pt-3 text-[11px] font-medium uppercase text-foundations-bold">
          all filters
        </li>
      )}

      {showOptions ? (
        choices?.length ? (
          choices.map((item, index) => {
            const [value, name] = item;
            const optionIndex = index + (recentOptions?.length || 0);
            const isHighlighted = highlightedIndex === optionIndex;
            const isSelectedValue = isSelected?.(value);

            return name ? (
              <li
                className={cn(
                  "flex cursor-pointer items-center justify-between rounded px-2 py-2 text-sm text-foundations-bolder",

                  !isMultiple &&
                    isSelectedValue &&
                    "bg-info-bold text-foundations-inverse",
                  isHighlighted && "bg-foundations-subtlest",

                  !isMultiple &&
                    isSelectedValue &&
                    isHighlighted &&
                    "bg-info-bolder"
                )}
                key={value}
                {...getItemProps?.({ item, index: optionIndex })}
              >
                {render ? (
                  render(name)
                ) : (
                  <>
                    <span>{snakeCaseToHumanReadable(name)}</span>
                    {isMultiple && (
                      <Checkbox
                        tabIndex={-1}
                        readOnly
                        checked={isSelected(value)}
                      />
                    )}
                  </>
                )}
              </li>
            ) : null;
          })
        ) : (
          <div className="my-4 flex flex-col items-center space-y-4">
            <NoMatchingResultIllustration />
            <p className="text-sm text-foundations-subtle">
              No matching data available
            </p>
          </div>
        )
      ) : (
        children
      )}
    </ul>
  );
};

const SearchField = ({
  filter,
  selectedValue,
  isMultiple,
  isSelected,
  handleSelect,
  setIsOpen,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 450);
  const { dependantFilter, filters } = useFiltersContext();
  const filterByFilter = filters.find(
    (f) => f.field_name === dependantFilter?.filterBy
  );

  const { data: options = [], isLoading } = useMoneyHashQuery({
    queryKey: [
      "filter",
      {
        name: filter.field_name,
        path: filter.path,
        searchTerm: debouncedSearchTerm,
        param: filter.search_param,
        dependantFilter,
        dependantFilterName: dependantFilter?.name,
        dependantFilterFilterBy: dependantFilter?.filterBy,
        filterByFilterSelectedValue: filterByFilter?.selected_value,
      },
    ],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get(`${process.env.NEXT_PUBLIC_BACKEND_URL}${filter.path}`, {
          params: {
            [filter.search_param]: debouncedSearchTerm,
            ...(dependantFilter &&
              filter.field_name === dependantFilter.name && {
                [dependantFilter.filterBy]:
                  filterByFilter?.selected_value || undefined,
              }),
            offset: 0,
            limit: 10,
          },
          signal,
        })
        .then((res) => ({
          data: Array.isArray(res.data.data) ? res.data.data : [],
        })),
    cacheTime: 60_000, // 1 minute
    enabled: !!debouncedSearchTerm,
  });

  return (
    <Combobox
      options={options}
      selectedValue={selectedValue}
      isMultiple={isMultiple}
      onSelect={(item) => handleSelect(item?.[0])}
      inputValue={searchTerm}
      onInputValueChange={setSearchTerm}
      selected={selectedValue}
      setIsOpen={setIsOpen}
    >
      <div className="px-4 pb-2">
        <Combobox.Input />
      </div>
      <div className="flex-1 overflow-y-auto">
        <Combobox.Options
          isSelected={isSelected}
          showOptions={debouncedSearchTerm && !isLoading}
        >
          {!debouncedSearchTerm ? (
            <div className="my-4 flex flex-col items-center space-y-4">
              <SearchIllustration />
              <p className="text-center text-sm text-foundations-subtle">
                Please type a search keyword
              </p>
            </div>
          ) : isLoading ? (
            <div className="my-4 flex flex-col items-center">
              <SearchIllustration />
            </div>
          ) : null}
        </Combobox.Options>
      </div>
    </Combobox>
  );
};

const multipleFieldsSet = new Set([
  "MultipleChoiceField",
  "ModelMultipleChoiceField",
  "MultiSelectSearchField",
]);

const fieldsWithoutFooterSet = new Set([
  "ChoiceField",
  "ModelChoiceField",
  "NullBooleanField",
  "DateField",
  "DateTimeField",
  "SearchField",
]);

const nullBooleanFieldOptions = [
  ["true", "Yes"],
  ["false", "No"],
];

const FilterInput = ({
  filter,
  value,
  onApplyFilter,
  shouldShowFooter,
  setOpen,
  isMultiple,
}) => {
  const searchInputRef = useRef(null);

  const updateFilter = (name, updatedValue) => {
    onApplyFilter(name, value === updatedValue ? "" : updatedValue);

    if (!shouldShowFooter) {
      setOpen(false);
    }
  };

  const isSelected = (optionValue) =>
    arrify(value)?.some?.((v) => v?.toString() === optionValue?.toString());

  const handleSelect = (selectedValue) => {
    if (!isMultiple) {
      updateFilter(filter.field_name, selectedValue);
      return;
    }

    if (isSelected(selectedValue)) {
      const selectedOptionsUpdated = arrify(value).filter(
        (v) => v !== selectedValue
      );

      updateFilter(filter.field_name, selectedOptionsUpdated);
      return;
    }

    const selectedOptionsUpdated = [...arrify(value), selectedValue];
    updateFilter(filter.field_name, selectedOptionsUpdated);
  };

  useEffect(() => {
    if (!searchInputRef.current) return;
    searchInputRef.current.focus();
  }, [searchInputRef]);

  if (filter.field_type === "CharField") {
    return (
      <div className="mb-2 px-4">
        <Input
          autoFocus
          type="text"
          placeholder="Add text..."
          value={value || ""}
          onChange={(value) => {
            updateFilter(filter.field_name, value);
          }}
        />
      </div>
    );
  } else if (filter.field_type === "DecimalField") {
    return (
      <div className="mb-2 px-4">
        <Input
          autoFocus
          type="number"
          value={value || ""}
          placeholder="Enter number..."
          onChange={(value) => {
            updateFilter(filter.field_name, value);
          }}
        />
      </div>
    );
  } else if (
    filter.field_type === "DateTimeField" ||
    filter.field_type === "DateField"
  ) {
    return (
      <DateField
        value={value}
        updateFilter={updateFilter}
        filter={filter}
        close={() => setOpen(false)}
      />
    );
  } else if (
    filter.field_type === "ChoiceField" ||
    filter.field_type === "MultipleChoiceField" ||
    filter.field_type === "ModelChoiceField" ||
    filter.field_type === "ModelMultipleChoiceField"
  ) {
    return (
      <Combobox
        isMultiple={isMultiple}
        options={filter.choices}
        filterFn={([_value, name], searchTerm) => {
          // NOTE: convert to string as some values are non-string
          return name
            ?.toString()
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase());
        }}
        selected={value}
        onSelect={(item) => {
          handleSelect(item?.[0]);
        }}
        setIsOpen={setOpen}
      >
        <div className="px-4 pb-2">
          <Combobox.Input />
        </div>

        <Combobox.Options isSelected={isSelected} />
      </Combobox>
    );
  } else if (filter.field_type === "NullBooleanField") {
    return (
      <Combobox
        options={nullBooleanFieldOptions}
        filterFn={([_value, name], searchTerm) => {
          // NOTE: convert to string as some values are non-string
          return name
            ?.toString()
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase());
        }}
        selected={value}
        onSelect={(item) => {
          handleSelect(item?.[0]);
        }}
        setIsOpen={setOpen}
      >
        <div className="px-4 pb-2">
          <Combobox.Input />
        </div>

        <Combobox.Options isSelected={isSelected} />
      </Combobox>
    );
  } else if (
    ["SearchField", "MultiSelectSearchField"].includes(filter.field_type)
  ) {
    return (
      <SearchField
        filter={filter}
        selectedValue={value}
        isSelected={isSelected}
        handleSelect={handleSelect}
        isMultiple={isMultiple}
        setIsOpen={setOpen}
      />
    );
  }
};

function FilterChip({ children, asChild, className, interactive }) {
  const Component = asChild ? Slot : "div";
  return (
    <Component
      className={cn(
        "border border-foundations-subtle bg-foundations-default px-2 py-1 outline-none first:rounded-l-lg last:rounded-r-lg",
        interactive &&
          "hover:z-10 hover:border-foundations-bold hover:bg-foundations-subtlest focus-visible:z-10 focus-visible:border-info-bold focus-visible:ring-1 focus-visible:ring-info-bold",
        className
      )}
    >
      {children}
    </Component>
  );
}

function FilterField({ filter, value, onRemoveFilter, onApplyFilter }) {
  const [isOpen, setIsOpen] = useState(false);
  const { nonClearableFilters } = useFiltersContext();
  const clearable = !nonClearableFilters?.includes(filter.field_name);

  const isMultiple = multipleFieldsSet.has(filter.field_type);

  const label =
    backendKeyMap[filter.field_name] ??
    snakeCaseToHumanReadable(filter.field_name);

  const displayedValue = useMemo(() => {
    if (!value) return null;

    if (filter.field_type === "SearchField") {
      return filter.value_lookup;
    }

    if (filter.field_type === "MultiSelectSearchField") {
      return filter.multiple_selected_values.map((v) => Object.values(v)[0]);
    }

    if (filter.field_type === "NullBooleanField")
      return nullBooleanFieldOptions.find((option) => option[0] === value)?.[1];

    if (!filter.choices) return value;

    // Get option label corresponding to it's value
    if (!isMultiple)
      return filter.choices.find(
        // NOTE: convert to string as some values are non-string
        (option) => option[0]?.toString() === value?.toString()
      )?.[1];

    return arrify(value).map(
      (v) =>
        filter.choices.find(
          // NOTE: convert to string as some values are non-string
          (option) => option[0]?.toString() === v?.toString()
        )?.[1]
    );
  }, [filter, value, isMultiple]);

  const Icon = filterIcons[filter.field_name];

  return (
    <div className="flex h-6 shrink-0 text-xs font-medium text-foundations-subtle [&>:not(:first-child)]:-ml-px">
      <FilterChip className="flex items-center gap-1 capitalize">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </FilterChip>

      <FilterChip>is</FilterChip>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <FilterChip interactive asChild className="text-foundations-bold">
          <PopoverTrigger>
            {displayedValue ? (
              <>
                {isMultiple ? (
                  <MultiplePreview value={arrify(displayedValue)} />
                ) : (
                  snakeCaseToHumanReadable(displayedValue)
                )}
              </>
            ) : (
              <span className="text-foundations-subtle">Select {label}</span>
            )}
          </PopoverTrigger>
        </FilterChip>

        <FilterPopover>
          <FilterFieldContent
            label={label}
            value={value}
            filter={filter}
            onApplyFilter={onApplyFilter}
            setIsOpen={setIsOpen}
          />
        </FilterPopover>
      </Popover>

      {clearable && displayedValue && (
        <FilterChip
          interactive
          asChild
          className="p-1 text-foundations-subtler"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFilter(filter.field_name);
            }}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </FilterChip>
      )}
    </div>
  );
}

const FilterPopover = ({ children, align = "start" }) => {
  return (
    <PopoverContent
      className="z-50 flex max-h-[var(--radix-popover-content-available-height)] min-w-80 max-w-80 flex-col overflow-hidden rounded-md bg-foundations-default shadow-2"
      align={align}
      side="bottom"
      sideOffset={8}
      collisionPadding={8}
    >
      {children}
    </PopoverContent>
  );
};

const FilterFieldContent = ({
  value,
  label,
  filter,
  onApplyFilter,
  setIsOpen,
}) => {
  const [tempValue, setTempValue] = useState(value);
  const isMultiple = multipleFieldsSet.has(filter.field_type);
  const shouldShowFooter = !fieldsWithoutFooterSet.has(filter.field_type);

  return (
    <>
      <div className="flex items-center justify-between p-4 pb-0">
        <div className="font-medium text-foundations-bolder">
          Filter by {label}
        </div>
        <PopoverClose className="text-foundations-subtler hover:text-foundations-subtle focus:text-info focus:outline-none">
          <XCircleIcon className="h-5 w-5" />
        </PopoverClose>
      </div>

      <form
        id={`form-${label}`}
        className={cn(
          "flex flex-1 flex-col overflow-hidden pt-2",
          shouldShowFooter ? "" : "pb-2"
        )}
        onSubmit={(e) => {
          e.preventDefault();
          onApplyFilter(filter.field_name, tempValue);
          setIsOpen(false);
        }}
      >
        <FilterInput
          filter={filter}
          value={tempValue}
          onApplyFilter={(filter, value) => {
            setTempValue(value);
            if (!shouldShowFooter) {
              onApplyFilter(filter, value);
            }
          }}
          shouldShowFooter={shouldShowFooter}
          setOpen={setIsOpen}
          isMultiple={isMultiple}
        />
      </form>
      {shouldShowFooter && (
        <div className="mt-1 flex gap-3 border-t border-foundations-subtle p-4">
          {isMultiple && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setTempValue("");
              }}
              className="flex-1"
            >
              Reset
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            form={`form-${label}`}
          >
            Apply
          </Button>
        </div>
      )}
    </>
  );
};

const MultiplePreview = ({ value }) => {
  if (!value?.length) return "";
  if (value.length === 1) {
    return value[0];
  }

  return (
    <>
      {value[0]} <span className="lowercase">and {value.length - 1} more</span>
    </>
  );
};

const dateRangeModes = {
  openIntervalDates: ["created_after", "created_before"],
  finiteIntervalDates: ["start_date", "end_date"],
};
const MAX_RECENT_FILTERS = 3;

const FiltersContext = createContext(null);

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error(
      "useFiltersContext must be used within <FiltersProvider />"
    );
  }

  return context;
};

const FiltersProvider = ({ children, value }) => {
  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

/**
 * Filters component
 *
 * @param {Object} props - Component props
 * @param {any} props.filters - The filters to display or apply
 * @param {string} props.recentStoreKey - Key used for storing/retrieving recent filters
 * @param {boolean} [props.enabledFilters] - Optional control showing/hiding filters functionality
 * @param {"openIntervalDates" | "finiteIntervalDates"} [props.dateRangeMode="openIntervalDates"] - Date filter mode
 * @param {"daily" | "monthly"} [props.datePickerMode="daily"] - Date picker mode
 * @param {"default" | "dashboard"} [props.filtersFor="default"] - Context for which filters apply
 * @param {string[]} [props.nonClearableFilters] - List of filter keys that cannot be cleared
 * @param {{name: string, filterBy: string}} [props.dependantFilter] - Optional filter dependency
 * @param {{enabled: boolean, placeholder: string}} [props.search] - Optional search functionality
 * @param {React.ReactNode} [props.leftSlot] - Optional React node to render in the left slot
 *
 */
export const Filters = ({
  filters,
  recentStoreKey,
  enabledFilters = true,
  dateRangeMode = "openIntervalDates",
  datePickerMode = "daily",
  filtersFor = "default",
  nonClearableFilters = [],
  dependantFilter,
  search,
  leftSlot,
}) => {
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [recentFilters, setRecentFilters] = useLocalStorage(
    `recent_filters_${recentStoreKey}`,
    []
  );

  const router = useRouter();
  const { featureFlags } = useFeatureFlags();

  const customDateFilters = useMemo(() => {
    return dateRangeModes[dateRangeMode] || [];
  }, [dateRangeMode]);

  const visibleFilters = useMemo(
    () =>
      filters.filter(
        (f) =>
          ![
            "created",
            "date_created",
            "is_live",
            ...(!featureFlags.dashboard_transaction_anomalies
              ? ["anomaly_detected"]
              : []),
          ].includes(f.field_name)
      ),
    [filters, featureFlags]
  );

  const isAnyFilterApplied = useMemo(
    () => filters.some(({ field_name }) => !!router.query[field_name]),
    [filters, router.query]
  );

  const applyFilter = (filter, value) => {
    if (!value) return removeFilter(filter);

    const cleanedValue = Array.isArray(value) ? value.filter(Boolean) : value;

    router.replace({
      query: {
        ...router.query,
        ...(filtersFor === "default" && { page: 1 }),
        [filter]: cleanedValue,
      },
    });
  };

  const removeFilter = (filter) => {
    const { [filter]: _queryFiltered, ...restQuery } = router.query;
    router.replace({
      query: { ...restQuery, ...(filtersFor === "default" && { page: 1 }) },
    });
  };

  const clearFilters = () => {
    const { search } = router.query;
    router.replace({
      query: {
        ...(filtersFor === "default" && { page: 1 }),
        ...(search ? { search } : {}), //persist search if exists
      },
    });
  };

  const filterOptions = useMemo(
    () =>
      visibleFilters
        .filter((filter) => !customDateFilters.includes(filter.field_name))
        .map((filter) => [
          filter.field_name,
          {
            ...filter,
            label:
              backendKeyMap[filter.field_name] ??
              snakeCaseToHumanReadable(filter.field_name),
          },
        ]),
    [visibleFilters, customDateFilters]
  );

  useEffect(() => {
    if (!open) {
      setActiveFilter(null);
    }
  }, [open]);

  const appliedFilters = useMemo(() => {
    const queryKeys = Object.keys(router.query);
    return queryKeys
      .filter((filter) => !customDateFilters.includes(filter))
      .map((key) => visibleFilters.find((filter) => filter.field_name === key))
      .filter(Boolean);
  }, [router.query, visibleFilters, customDateFilters]);

  const isCustomDateFilterApplied = useMemo(() => {
    return customDateFilters.some((filter) => filter in router.query);
  }, [router.query, customDateFilters]);

  const withCustomDateFilter = useMemo(() => {
    return customDateFilters.every((filter) =>
      filters.find((f) => f.field_name === filter)
    );
  }, [filters, customDateFilters]);

  return (
    <FiltersProvider
      value={{
        filters,
        recentStoreKey,
        customDateFilters,
        dateRangeMode,
        datePickerMode,
        filtersFor,
        nonClearableFilters,
        dependantFilter,
      }}
    >
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-wrap gap-4">
          {leftSlot}

          {search?.enabled && <GlobalSearch placeholder={search.placeholder} />}

          {enabledFilters && visibleFilters.length > 0 && (
            <>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                  className={cn(
                    "group flex h-8 shrink-0 items-center gap-1 rounded-md border border-foundations-subtle px-2 py-1 text-xs font-medium text-foundations-subtle outline-none hover:border-foundations-bold hover:bg-foundations-subtlest focus:border-info-bold focus:text-info focus:ring-1 focus:ring-info-bold ",
                    open && "!border-info-bold text-info ring-1 ring-info-bold"
                  )}
                >
                  <FilterIcon
                    className={cn(
                      "size-4",
                      open
                        ? "text-info"
                        : "text-foundations-subtler group-hover:text-foundations-subtle group-focus:text-info"
                    )}
                  />{" "}
                  Filters
                </PopoverTrigger>

                <FilterPopover align={search?.enabled ? "end" : "start"}>
                  {!activeFilter && (
                    <FilterSearchPopoverContent
                      recentFilters={recentFilters}
                      options={filterOptions}
                      onSelect={(filter) => {
                        setActiveFilter(filter);
                        setRecentFilters(
                          [
                            ...new Set([filter.field_name, ...recentFilters]),
                          ].slice(0, MAX_RECENT_FILTERS)
                        );
                      }}
                      setIsOpen={setOpen}
                    />
                  )}

                  {activeFilter && (
                    <FilterFieldContent
                      label={activeFilter.label}
                      value={router.query[activeFilter.field_name]}
                      filter={activeFilter}
                      onApplyFilter={applyFilter}
                      setIsOpen={setOpen}
                    />
                  )}
                </FilterPopover>
              </Popover>

              {withCustomDateFilter && (
                <DateFilters onApplyFilter={applyFilter} />
              )}
            </>
          )}
        </div>

        {enabledFilters && isAnyFilterApplied && (
          <div className="flex flex-wrap items-start gap-2">
            {appliedFilters.map((filter, index) => (
              <FilterField
                key={filter.field_type + index}
                filter={filter}
                value={router.query[filter.field_name]}
                onRemoveFilter={removeFilter}
                onApplyFilter={applyFilter}
              />
            ))}

            {isCustomDateFilterApplied && <CustomDateFilterField />}

            {isAnyFilterApplied && (
              <Button
                variant="tertiary"
                className="flex h-6 items-center px-3 py-0.5 text-xs font-medium text-info hover:bg-foundations-subtlest/70 hover:text-info"
                onClick={clearFilters}
              >
                <XIcon className="!h-4 !w-4" />
                Clear filters
              </Button>
            )}
          </div>
        )}
      </div>
    </FiltersProvider>
  );
};

const useOpenIntervalDates = () => {
  const router = useRouter();
  const createdAfter = router.query.created_after;
  const createdBefore = router.query.created_before;

  const {
    today,
    lastWeek,
    last30Days,
    lastMonth,
    lastMonthEnd,
    lastQuarter,
    lastQuarterEnd,
  } = useMemo(() => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentQuarterStart = new Date(
      now.getFullYear(),
      Math.floor(now.getMonth() / 3) * 3,
      1
    );
    const lastQuarterStart = subMonths(currentQuarterStart, 3);
    const lastQuarterEnd = subDays(currentQuarterStart, 1);
    return {
      today: format(now, useDateInput.valueFormatString),
      lastWeek: format(subDays(now, 7), useDateInput.valueFormatString),
      last30Days: format(subDays(now, 30), useDateInput.valueFormatString),
      lastMonth: format(
        subMonths(currentMonthStart, 1),
        useDateInput.monthValueFormatString
      ),
      lastMonthEnd: format(
        subDays(currentMonthStart, 1),
        useDateInput.monthValueFormatString
      ),
      lastQuarter: format(
        lastQuarterStart,
        useDateInput.monthValueFormatString
      ),
      lastQuarterEnd: format(
        lastQuarterEnd,
        useDateInput.monthValueFormatString
      ),
    };
  }, []);

  const isTodaySelected = !createdBefore && createdAfter === today;
  const isLastWeekSelected = !createdBefore && createdAfter === lastWeek;
  const isLast30DaysSelected = !createdBefore && createdAfter === last30Days;
  const isLastMonthSelected = !createdBefore && createdAfter === lastMonth;
  const isLastQuarterSelected = !createdBefore && createdAfter === lastQuarter;
  const isCustomSelected =
    !isTodaySelected &&
    !isLastWeekSelected &&
    !isLast30DaysSelected &&
    !isLastMonthSelected &&
    !isLastQuarterSelected &&
    (createdBefore || createdAfter);

  return {
    createdBefore,
    createdAfter,
    today,
    lastWeek,
    last30Days,
    lastMonth,
    lastMonthEnd,
    lastQuarter,
    lastQuarterEnd,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isLastMonthSelected,
    isLastQuarterSelected,
    isCustomSelected,
  };
};

const useFiniteIntervalDates = () => {
  const router = useRouter();
  const startDate = router.query.start_date;
  const endDate = router.query.end_date;

  const {
    today,
    lastWeek,
    last30Days,
    lastMonth,
    lastMonthEnd,
    lastQuarter,
    lastQuarterEnd,
  } = useMemo(() => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentQuarterStart = new Date(
      now.getFullYear(),
      Math.floor(now.getMonth() / 3) * 3,
      1
    );
    const lastQuarterStart = subMonths(currentQuarterStart, 3);
    const lastQuarterEnd = subDays(currentQuarterStart, 1);
    return {
      today: format(now, useDateInput.valueFormatString),
      lastWeek: format(subDays(now, 7), useDateInput.valueFormatString),
      last30Days: format(subDays(now, 30), useDateInput.valueFormatString),
      lastMonth: format(
        subMonths(currentMonthStart, 1),
        useDateInput.monthValueFormatString
      ),
      lastMonthEnd: format(
        subDays(currentMonthStart, 1),
        useDateInput.monthValueFormatString
      ),
      lastQuarter: format(
        lastQuarterStart,
        useDateInput.monthValueFormatString
      ),
      lastQuarterEnd: format(
        lastQuarterEnd,
        useDateInput.monthValueFormatString
      ),
    };
  }, []);

  const isTodaySelected = startDate === today && endDate === today;
  const isLastWeekSelected = startDate === lastWeek && endDate === today;
  const isLast30DaysSelected = startDate === last30Days && endDate === today;
  const isLastMonthSelected =
    startDate === lastMonth && endDate === lastMonthEnd;
  const isLastQuarterSelected =
    startDate === lastQuarter && endDate === lastQuarterEnd;
  const isCustomSelected =
    !isTodaySelected &&
    !isLastWeekSelected &&
    !isLast30DaysSelected &&
    !isLastMonthSelected &&
    !isLastQuarterSelected &&
    (startDate || endDate);

  return {
    startDate,
    endDate,
    today,
    lastWeek,
    last30Days,
    lastMonth,
    lastMonthEnd,
    lastQuarter,
    lastQuarterEnd,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isLastMonthSelected,
    isLastQuarterSelected,
    isCustomSelected,
  };
};

const DateRangeModeHooks = {
  finiteIntervalDates: useFiniteIntervalDates,
  openIntervalDates: useOpenIntervalDates,
};

const CustomDateFilterField = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const { filtersFor, dateRangeMode, nonClearableFilters } =
    useFiltersContext();

  const {
    createdBefore,
    createdAfter,
    startDate,
    endDate,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isCustomSelected,
  } = DateRangeModeHooks[dateRangeMode]();

  const { operator, formattedDate } = useCustomDateFormat({
    from: dateRangeMode === "openIntervalDates" ? createdAfter : startDate,
    to: dateRangeMode === "openIntervalDates" ? createdBefore : endDate,
  });

  return (
    <div className="flex h-6 shrink-0 text-xs font-medium text-foundations-subtle [&>:not(:first-child)]:-ml-px">
      <FilterChip className="flex items-center gap-1 capitalize">
        <ClockIcon className="h-4 w-4" />
        Date
      </FilterChip>

      <FilterChip>{isCustomSelected ? operator : "is"}</FilterChip>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <FilterChip interactive asChild className="text-foundations-bold">
          <PopoverTrigger>
            {isTodaySelected
              ? "Today"
              : isLastWeekSelected
                ? "Last week"
                : isLast30DaysSelected
                  ? "Last 30 days"
                  : formattedDate}
          </PopoverTrigger>
        </FilterChip>

        <FilterPopover>
          <CustomDatePopoverContent
            createdAfter={
              dateRangeMode === "openIntervalDates" ? createdAfter : startDate
            }
            createdBefore={
              dateRangeMode === "openIntervalDates" ? createdBefore : endDate
            }
            onClose={() => setIsOpen(false)}
          />
        </FilterPopover>
      </Popover>

      {!!nonClearableFilters &&
      (nonClearableFilters.includes("start_date") ||
        nonClearableFilters.includes("end_date") ||
        nonClearableFilters.includes("created_after") ||
        nonClearableFilters.includes("created_before")) ? null : (
        <FilterChip
          interactive
          asChild
          className="p-1 text-foundations-subtler"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const {
                created_after: _after,
                created_before: _before,
                start_date: _start,
                end_date: _end,
                ...restQuery
              } = router.query;
              router.replace({
                query: {
                  ...restQuery,
                  ...(filtersFor === "default" && { page: 1 }),
                },
              });
            }}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </FilterChip>
      )}
    </div>
  );
};

const FilterSearchPopoverContent = ({
  options,
  onSelect,
  recentFilters,
  setIsOpen,
}) => {
  return (
    <Combobox
      options={options}
      recentFilters={recentFilters}
      itemToString={(item) => item?.[1].label}
      filterFn={([_value, { label }], searchTerm) => {
        return label
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      }}
      onSelect={([_, filter]) => onSelect(filter)}
      setIsOpen={setIsOpen}
    >
      <div className="flex items-center gap-3 px-4 pb-1 pt-3">
        <Combobox.Input />

        <PopoverClose className="text-foundations-subtler hover:text-foundations-subtle focus:text-info focus:outline-none">
          <XCircleIcon className="h-5 w-5" />
        </PopoverClose>
      </div>

      <div className="flex-1 scroll-pb-2  overflow-y-auto pb-2 pt-0.5">
        <section>
          <Combobox.Options renderOption={FilterOption} />
        </section>
      </div>
    </Combobox>
  );
};

const FilterOption = ({ field_name, label }) => {
  const Icon = filterIcons[field_name];
  return (
    <div className="flex items-center gap-3 capitalize ">
      {Icon && <Icon className="h-5 w-5 shrink-0 text-foundations-subtler" />}
      {label}
    </div>
  );
};

/**
 * InPlaceFilters component
 *
 * @param {Object} props - Component props
 * @param {any} props.filters - The filters to display or apply
 * @param {string} [props.recentStoreKey] - Key used for storing/retrieving recent filters
 * @param {boolean} [props.showLabel=true] - Whether to show the "Filter:" label
 * @param {boolean} [props.showClearFilters=true] - Whether to show the "Clear filters" button
 * @param {Object} props.appliedFilters - The currently applied filters
 * @param {Function} props.customApplyFilter - Function to apply a filter
 * @param {Function} props.customRemoveFilter - Function to remove a filter
 * @param {Function} props.customClearFilters - Function to clear all filters
 * @param {"openIntervalDates" | "finiteIntervalDates"} [props.dateRangeMode="openIntervalDates"] - Date filter mode
 * @param {"daily" | "monthly"} [props.datePickerMode="daily"] - Date picker mode
 * @param {"default" | "dashboard"} [props.filtersFor="default"] - Context for which filters apply
 * @param {string[]} [props.nonClearableFilters] - List of filter keys that cannot be cleared
 * @param {{name: string, filterBy: string}} [props.dependantFilter] - Optional filter dependency
 */
export const InPlaceFilters = ({
  filters,
  showLabel = true,
  showClearFilters = true,
  dateRangeMode = "openIntervalDates",
  datePickerMode = "daily",
  appliedFilters,
  recentStoreKey,
  filtersFor = "default",
  nonClearableFilters = [],
  customApplyFilter,
  customRemoveFilter,
  customClearFilters,
  dependantFilter,
}) => {
  const customDateFilters = useMemo(() => {
    return dateRangeModes[dateRangeMode] || [];
  }, [dateRangeMode]);

  const visibleFilters = useMemo(
    () =>
      filters.filter(
        (f) => !["created", "date_created", "is_live"].includes(f.field_name)
      ),
    [filters]
  );

  const applyFilter = (filter, value) => {
    if (!value) return removeFilter(filter);
    customApplyFilter(filter, value);
  };

  const removeFilter = (filter) => {
    customRemoveFilter(filter);
  };

  const clearFilters = () => {
    customClearFilters();
  };

  if (!visibleFilters.length) return null;

  return (
    <FiltersProvider
      value={{
        filters,
        recentStoreKey,
        customDateFilters,
        dateRangeMode,
        datePickerMode,
        filtersFor,
        nonClearableFilters,
        dependantFilter,
      }}
    >
      <div className="flex flex-1 items-center">
        {showLabel && (
          <div className="mr-2 mt-1 text-foundations-subtler">Filter:</div>
        )}
        <div className="flex flex-1 flex-wrap gap-2">
          {visibleFilters.map((filter, index) => (
            <FilterField
              key={filter.field_type + index}
              filter={filter}
              value={appliedFilters[filter.field_name]}
              onRemoveFilter={removeFilter}
              onApplyFilter={applyFilter}
            />
          ))}
        </div>
        <div className="flex items-center justify-end text-foundations-subtle hover:text-foundations-bold">
          {showClearFilters && (
            <button className="flex items-center" onClick={clearFilters}>
              <XIcon className="h-5 text-xs" />{" "}
              <span className="pl-1">Clear filters</span>
            </button>
          )}
        </div>
      </div>
    </FiltersProvider>
  );
};

const DateField = ({ value, updateFilter, filter }) => {
  const { inputRef, inputProps, setInputValue, month, setMonth } = useDateInput(
    {
      onSelect: (day) => updateFilter(filter.field_name, day),
      selected: value,
    }
  );

  const handleDaySelect = (date) => {
    if (date) {
      updateFilter(
        filter.field_name,
        format(date, useDateInput.valueFormatString)
      );
    } else {
      updateFilter(filter.field_name, "");
      setInputValue("");
    }
  };

  return (
    <div className="flex flex-col">
      <DateInput
        ref={inputRef}
        className="px-2"
        icon={<CalendarIcon className="h-5 w-5" />}
        {...inputProps}
      />
      <Calendar
        className="self-center"
        mode="single"
        selected={
          value
            ? parse(value, useDateInput.valueFormatString, new Date())
            : undefined
        }
        onSelect={handleDaySelect}
        disablePortal
        month={month}
        onMonthChange={setMonth}
      />
    </div>
  );
};

const DateFilters = () => {
  const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
  const router = useRouter();
  const { dateRangeMode, datePickerMode } = useFiltersContext();

  const {
    createdAfter,
    createdBefore,
    startDate,
    endDate,
    today,
    lastWeek,
    last30Days,
    lastMonth,
    lastMonthEnd,
    lastQuarter,
    lastQuarterEnd,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isLastMonthSelected,
    isLastQuarterSelected,
    isCustomSelected,
  } = DateRangeModeHooks[dateRangeMode]();

  return (
    <div className="relative ml-auto flex shrink-0 gap-2 rounded-md border border-foundations-subtle p-1 text-foundations-subtle">
      <UTCLabel />
      <Popover open={isCustomPopoverOpen} onOpenChange={setIsCustomPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "relative z-10 flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium hover:bg-info-subtler hover:text-info",
              isCustomSelected && "bg-info-subtler text-info"
            )}
          >
            {isCustomSelected ? (
              <>
                <CalendarIcon className="h-4 w-4" />{" "}
                <CustomDateFormatted
                  from={
                    dateRangeMode === "openIntervalDates"
                      ? createdAfter
                      : startDate
                  }
                  to={
                    dateRangeMode === "openIntervalDates"
                      ? createdBefore
                      : endDate
                  }
                />
              </>
            ) : (
              "Custom"
            )}
          </button>
        </PopoverTrigger>
        <FilterPopover align="center">
          <CustomDatePopoverContent
            createdAfter={
              dateRangeMode === "openIntervalDates" ? createdAfter : startDate
            }
            createdBefore={
              dateRangeMode === "openIntervalDates" ? createdBefore : endDate
            }
            onClose={() => setIsCustomPopoverOpen(false)}
          />
        </FilterPopover>
      </Popover>

      {datePickerMode === "daily" ? (
        <>
          <button
            type="button"
            className={cn(
              "relative z-10 rounded px-1 py-0.5 text-xs font-medium hover:bg-info-subtler hover:text-info",
              isTodaySelected && "bg-info-subtler text-info"
            )}
            onClick={() => {
              if (dateRangeMode === "finiteIntervalDates") {
                const {
                  start_date: _start,
                  end_date: _end,
                  ...restQuery
                } = router.query;
                router.replace({
                  query: {
                    ...restQuery,
                    start_date: today,
                    end_date: today,
                  },
                });
                return;
              }

              const { created_before: _before, ...restQuery } = router.query;
              router.replace({
                query: {
                  ...restQuery,
                  created_after: today,
                  page: 1,
                },
              });
            }}
          >
            Today
          </button>
          <button
            type="button"
            className={cn(
              "rounded px-1 py-0.5 text-xs font-medium hover:bg-info-subtler hover:text-info",
              isLastWeekSelected && "bg-info-subtler text-info"
            )}
            onClick={() => {
              if (dateRangeMode === "finiteIntervalDates") {
                const {
                  start_date: _start,
                  end_date: _end,
                  ...restQuery
                } = router.query;
                router.replace({
                  query: {
                    ...restQuery,
                    start_date: lastWeek,
                    end_date: today,
                  },
                });
                return;
              }

              const { created_before: _before, ...restQuery } = router.query;
              router.replace({
                query: {
                  ...restQuery,
                  created_after: lastWeek,
                  page: 1,
                },
              });
            }}
          >
            Last week
          </button>
          <button
            type="button"
            className={cn(
              "rounded px-1 py-0.5 text-xs font-medium hover:bg-info-subtler hover:text-info",
              isLast30DaysSelected && "bg-info-subtler text-info"
            )}
            onClick={() => {
              if (dateRangeMode === "finiteIntervalDates") {
                const {
                  start_date: _start,
                  end_date: _end,
                  ...restQuery
                } = router.query;
                router.replace({
                  query: {
                    ...restQuery,
                    start_date: last30Days,
                    end_date: today,
                  },
                });
                return;
              }

              const { created_before: _before, ...restQuery } = router.query;
              router.replace({
                query: {
                  ...restQuery,
                  created_after: last30Days,
                  page: 1,
                },
              });
            }}
          >
            Last 30 days
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className={cn(
              "rounded px-1 py-0.5 text-xs font-medium hover:bg-info-subtler hover:text-info",
              isLastMonthSelected && "bg-info-subtler text-info"
            )}
            onClick={() => {
              if (dateRangeMode === "finiteIntervalDates") {
                const {
                  start_date: _start,
                  end_date: _end,
                  ...restQuery
                } = router.query;
                router.replace({
                  query: {
                    ...restQuery,
                    start_date: lastMonth,
                    end_date: lastMonthEnd,
                  },
                });
                return;
              }

              const { created_before: _before, ...restQuery } = router.query;
              router.replace({
                query: {
                  ...restQuery,
                  created_after: lastMonth,
                  page: 1,
                },
              });
            }}
          >
            Last month
          </button>
          <button
            type="button"
            className={cn(
              "rounded px-1 py-0.5 text-xs font-medium hover:bg-info-subtler hover:text-info",
              isLastQuarterSelected && "bg-info-subtler text-info"
            )}
            onClick={() => {
              if (dateRangeMode === "finiteIntervalDates") {
                const {
                  start_date: _start,
                  end_date: _end,
                  ...restQuery
                } = router.query;
                router.replace({
                  query: {
                    ...restQuery,
                    start_date: lastQuarter,
                    end_date: lastQuarterEnd,
                  },
                });
                return;
              }

              const { created_before: _before, ...restQuery } = router.query;
              router.replace({
                query: {
                  ...restQuery,
                  created_after: lastQuarter,
                  page: 1,
                },
              });
            }}
          >
            Last quarter
          </button>
        </>
      )}
    </div>
  );
};

const useCustomDateFormat = ({ from, to }) => {
  const { datePickerMode } = useFiltersContext();

  if (!from) {
    const endDate = parse(to, useDateInput.valueFormatString, new Date());
    return {
      operator: "before",
      formattedDate: `${format(endDate, "dd MMM yyyy")}`,
    };
  }

  if (!to) {
    const startDate = parse(from, useDateInput.valueFormatString, new Date());
    return {
      operator: "after",
      formattedDate: `${format(startDate, "dd MMM yyyy")}`,
    };
  }

  const valueFormatString =
    datePickerMode === "monthly"
      ? useDateInput.monthValueFormatString
      : useDateInput.valueFormatString;

  const startDate = parse(from, valueFormatString, new Date());
  const endDate = parse(to, valueFormatString, new Date());
  const sameYear = isSameYear(startDate, endDate);

  const includeDaysInFormat = datePickerMode === "monthly" ? "" : "dd";

  return {
    operator: "between",
    formattedDate: `${format(
      startDate,
      `${includeDaysInFormat} MMM ${sameYear ? "" : "yyyy"}`
    )} - ${format(
      endDate,
      `${includeDaysInFormat} MMM${sameYear ? ", yyyy" : " yyyy"}`
    )}`,
  };
};

const useUTCValue = () => {
  const utcOffsetFormatted = useMemo(() => {
    const offsetMinutes = -new Date().getTimezoneOffset();

    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);

    const hours = String(Math.floor(abs / 60)).padStart(2, "0");
    const minutes = String(abs % 60).padStart(2, "0");

    const utcOffsetFormatted = `${sign}${hours}:${minutes}`;
    return utcOffsetFormatted;
  }, []);
  return utcOffsetFormatted;
};

const UTCLabel = () => {
  const utcValue = useUTCValue();

  return (
    <span className="absolute left-1 top-[-3px] -translate-y-1/2 bg-foundations-default px-1 text-[10px] font-medium tracking-[0.5px] text-foundations-subtle">
      UTC{utcValue}
    </span>
  );
};

const CustomDateFormatted = ({ from, to }) => {
  const { operator, formattedDate } = useCustomDateFormat({ from, to });
  if (operator === "between") return formattedDate;
  return `${operator} ${formattedDate}`;
};

const CustomDatePopoverContent = ({ createdBefore, createdAfter, onClose }) => {
  const router = useRouter();
  const { dateRangeMode, datePickerMode } = useFiltersContext();
  const defaultTabOpen = useMemo(() => {
    if (createdBefore && createdAfter) return 0;
    if (createdBefore) return 1;
    if (createdAfter) return 2;
    return 0;
  }, [createdBefore, createdAfter]);

  const utcValue = useUTCValue();

  return datePickerMode === "monthly" ? (
    <MonthlyRangeDate from={createdAfter} to={createdBefore} />
  ) : (
    <Tab.Group defaultIndex={defaultTabOpen}>
      <Tab.List className="flex items-center justify-center gap-4 border-b border-foundations-subtle text-sm font-medium">
        <Tab className="-mb-px border-b-2 border-transparent px-2 py-2.5 text-foundations-subtle aria-selected:border-info-bold aria-selected:text-foundations-bolder">
          Range
        </Tab>
        {dateRangeMode === "openIntervalDates" && (
          <>
            <Tab className="-mb-px border-b-2 border-transparent px-2 py-2.5 text-foundations-subtle aria-selected:border-info-bold aria-selected:text-foundations-bolder">
              Before
            </Tab>
            <Tab className="-mb-px border-b-2 border-transparent px-2 py-2.5 text-foundations-subtle aria-selected:border-info-bold aria-selected:text-foundations-bolder">
              After
            </Tab>
          </>
        )}
      </Tab.List>

      <Tab.Panels className="pt-4">
        <Tab.Panel className="flex flex-col gap-4">
          <RangeCustomDate from={createdAfter} to={createdBefore} />
        </Tab.Panel>
        {dateRangeMode === "openIntervalDates" && (
          <>
            <Tab.Panel className="flex flex-col gap-4">
              <SingleCustomDate
                value={createdBefore}
                label="Before"
                onChange={(value) => {
                  const {
                    created_after: _after,
                    created_before: _before,
                    ...restQuery
                  } = router.query;
                  if (value) {
                    router.replace({
                      query: {
                        ...restQuery,
                        created_before: value,
                        page: 1,
                      },
                    });
                  } else {
                    router.replace({
                      query: {
                        ...restQuery,
                        page: 1,
                      },
                    });
                  }
                }}
                onClose={onClose}
              />
            </Tab.Panel>
            <Tab.Panel className="flex flex-col gap-4">
              <SingleCustomDate
                value={createdAfter}
                label="After"
                onChange={(value) => {
                  const {
                    created_before: _before,
                    created_after: _after,
                    ...restQuery
                  } = router.query;
                  if (value) {
                    router.replace({
                      query: {
                        ...restQuery,
                        created_after: value,
                        page: 1,
                      },
                    });
                  } else {
                    router.replace({
                      query: {
                        ...restQuery,
                        page: 1,
                      },
                    });
                  }
                }}
                onClose={onClose}
              />
            </Tab.Panel>
          </>
        )}
      </Tab.Panels>

      <div className="flex items-center gap-1.5 p-4">
        <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foundations-subtlest text-foundations-subtler">
          <InformationCircleIcon className="size-4" />
        </div>
        <div className="text-xs text-foundations-subtle">
          <p>Dates and time shown are UTC-based.</p>
          <p>Your timing will be {utcValue} UTC.</p>
        </div>
      </div>
    </Tab.Group>
  );
};

const SingleCustomDate = ({ value, label, onChange, onClose }) => {
  const { inputRef, inputProps, setInputValue, month, setMonth } = useDateInput(
    {
      onSelect: (day) => onChange(day),
      selected: value,
    }
  );

  const handleDaySelect = (date) => {
    if (date) {
      onChange(format(date, useDateInput.valueFormatString));
    } else {
      onChange("");
      setInputValue("");
    }
    onClose();
  };

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (isValidDate(inputProps.value)) {
        const date = parse(
          inputProps.value,
          useDateInput.inputFormatString,
          new Date()
        );
        onChange(format(date, useDateInput.valueFormatString));
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [inputProps.value]);

  return (
    <>
      <div className="flex items-center gap-1 self-center">
        <p className="w-24  text-sm font-medium text-foundations-bold">
          {label} date
        </p>
        <DateInput
          ref={inputRef}
          {...inputProps}
          className="w-full flex-1"
          placeholder="Date"
        />
      </div>
      <Calendar
        className="self-center py-0"
        mode="single"
        selected={
          value
            ? parse(value, useDateInput.valueFormatString, new Date())
            : undefined
        }
        onSelect={handleDaySelect}
        disablePortal
        month={month}
        onMonthChange={setMonth}
      />
    </>
  );
};

const RangeCustomDate = ({ from, to }) => {
  const router = useRouter();
  const { filtersFor, dateRangeMode } = useFiltersContext();
  const [selectedDateRange, setSelectedDateRange] = useState(() => ({
    from: from
      ? parse(from, useDateInput.valueFormatString, new Date())
      : undefined,
    to: to ? parse(to, useDateInput.valueFormatString, new Date()) : undefined,
  }));

  const {
    inputRef: startInputRef,
    inputProps: startInputProps,
    month,
    setMonth,
  } = useDateInput({
    selected: selectedDateRange?.from
      ? format(selectedDateRange.from, useDateInput.valueFormatString)
      : undefined,
  });
  const { inputRef: endInputRef, inputProps: endInputProps } = useDateInput({
    selected: selectedDateRange?.to
      ? format(selectedDateRange.to, useDateInput.valueFormatString)
      : undefined,
  });

  useUpdateEffect(() => {
    if (selectedDateRange?.from && selectedDateRange?.to) {
      router.replace({
        query: {
          ...router.query,
          ...(dateRangeMode === "openIntervalDates"
            ? {
                created_after: format(
                  selectedDateRange.from,
                  useDateInput.valueFormatString
                ),
                created_before: format(
                  selectedDateRange.to,
                  useDateInput.valueFormatString
                ),
              }
            : {
                start_date: format(
                  selectedDateRange.from,
                  useDateInput.valueFormatString
                ),
                end_date: format(
                  selectedDateRange.to,
                  useDateInput.valueFormatString
                ),
              }),
        },
      });
    }
  }, [selectedDateRange]);

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (isValidDate(startInputProps.value)) {
        const date = parse(
          startInputProps.value,
          useDateInput.inputFormatString,
          new Date()
        );
        setSelectedDateRange((prev) => ({
          ...prev,
          from: date,
        }));
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [startInputProps.value]);

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (isValidDate(endInputProps.value)) {
        const date = parse(
          endInputProps.value,
          useDateInput.inputFormatString,
          new Date()
        );

        setSelectedDateRange((prev) => ({
          ...prev,
          to: date,
        }));
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [endInputProps.value]);

  return (
    <>
      <div className="flex items-center gap-1 self-center">
        <p className="w-24  text-sm font-medium text-foundations-bold">
          Start date
        </p>
        <DateInput
          ref={startInputRef}
          {...startInputProps}
          className="w-full flex-1"
          placeholder="Date"
        />
      </div>
      <div className="flex items-center gap-1 self-center">
        <p className="w-24  text-sm font-medium text-foundations-bold">
          End date
        </p>
        <DateInput
          ref={endInputRef}
          {...endInputProps}
          className="w-full flex-1"
          placeholder="Date"
        />
      </div>
      <Calendar
        className="self-center py-0"
        mode="range"
        selected={selectedDateRange}
        onSelect={(value) => {
          if (!value) {
            const {
              created_after: _after,
              created_before: _before,
              start_date: _start,
              end_date: _end,
              page: _page,
              ...restQuery
            } = router.query;
            router.replace({
              query: {
                ...restQuery,
                ...(filtersFor === "default" && { page: 1 }),
              },
            });
          }
          setSelectedDateRange(value);
        }}
        disablePortal
        month={month}
        onMonthChange={setMonth}
        toDate={new Date()}
      />
    </>
  );
};

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MonthlyRangeDate = ({ from, to }) => {
  const router = useRouter();

  const { dateRangeMode } = useFiltersContext();

  const [selectedRange, setSelectedRange] = useState(() => {
    const fromDate = from
      ? parse(from, useDateInput.monthValueFormatString, new Date())
      : undefined;
    const toDate = to
      ? parse(to, useDateInput.monthValueFormatString, new Date())
      : undefined;
    return {
      from: fromDate
        ? new Date(fromDate.getFullYear(), fromDate.getMonth(), 1)
        : undefined,
      to: toDate
        ? new Date(toDate.getFullYear(), toDate.getMonth(), 1)
        : undefined,
    };
  });
  const [year, setYear] = useState(() => {
    const selectedDate = selectedRange.to || selectedRange.from || new Date();
    return selectedDate.getFullYear();
  });

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();

  useUpdateEffect(() => {
    if (!selectedRange?.from || !selectedRange?.to) return;
    const startDate = new Date(
      selectedRange.from.getFullYear(),
      selectedRange.from.getMonth(),
      1
    );
    const endDate = new Date(
      selectedRange.to.getFullYear(),
      selectedRange.to.getMonth() + 1,
      0
    );
    router.replace({
      query: {
        ...router.query,
        ...(dateRangeMode === "openIntervalDates"
          ? {
              created_after: format(
                startDate,
                useDateInput.monthValueFormatString
              ),
              created_before: format(
                endDate,
                useDateInput.monthValueFormatString
              ),
            }
          : {
              start_date: format(
                startDate,
                useDateInput.monthValueFormatString
              ),
              end_date: format(endDate, useDateInput.monthValueFormatString),
            }),
      },
    });
  }, [selectedRange, dateRangeMode]);

  const handleMonthSelect = (monthIndex) => {
    const monthDate = new Date(year, monthIndex, 1);
    const isFutureMonth =
      year > currentYear ||
      (year === currentYear && monthIndex > currentMonthIndex);
    if (isFutureMonth) return;

    setSelectedRange((prev) => {
      if (!prev?.from || (prev?.from && prev?.to)) {
        return { from: monthDate, to: undefined };
      }

      if (monthDate.getTime() < prev.from.getTime()) {
        return { from: monthDate, to: prev.from };
      }

      return { from: prev.from, to: monthDate };
    });
  };

  const isSameMonth = (first, second) =>
    first &&
    second &&
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth();

  const isInRange = (monthDate) => {
    if (!selectedRange?.from || !selectedRange?.to) return false;
    return (
      monthDate.getTime() >= selectedRange.from.getTime() &&
      monthDate.getTime() <= selectedRange.to.getTime()
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded text-foundations-subtle hover:bg-foundations-subtlest"
          aria-label="Go to previous year"
          onClick={() => setYear((prev) => prev - 1)}
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
        <span className="text-sm font-medium text-foundations-boldest">
          {year}
        </span>
        <button
          type="button"
          className="rounded text-foundations-subtle hover:bg-foundations-subtlest disabled:cursor-not-allowed disabled:text-foundations-subtle"
          aria-label="Go to next year"
          onClick={() => setYear((prev) => prev + 1)}
          disabled={year >= currentYear}
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-y-2">
        {MONTH_LABELS.map((label, monthIndex) => {
          const monthDate = new Date(year, monthIndex, 1);
          const isFutureMonth =
            year > currentYear ||
            (year === currentYear && monthIndex > currentMonthIndex);
          const isStart = isSameMonth(monthDate, selectedRange.from);
          const isEnd = isSameMonth(monthDate, selectedRange.to);
          const isRange = isInRange(monthDate);

          const isFirstInRow = monthIndex % 4 === 0;
          const isLastInRow = monthIndex % 4 === 3;

          return (
            <div
              key={`${year}-${label}`}
              className={cn(
                "px-2",
                isRange && "bg-info-bold",
                (isStart || isFirstInRow) && "rounded-l-full",
                (isEnd || isLastInRow) && "rounded-r-full"
              )}
            >
              <button
                type="button"
                className={cn(
                  "w-full rounded px-2 py-1 text-sm text-foundations-bolder",
                  "hover:bg-foundations-subtlest hover:text-foundations-bolder focus:outline-none focus:ring-2 focus:ring-info-bold",
                  isRange &&
                    "rounded-none bg-info-bold text-foundations-inverse hover:bg-info-bold hover:text-foundations-inverse",
                  (isStart || isFirstInRow) && "rounded-l-full",
                  (isEnd || isLastInRow) && "rounded-r-full",
                  isFutureMonth &&
                    "cursor-not-allowed text-neutral-300 hover:bg-foundations-default hover:text-neutral-300"
                )}
                onClick={() => handleMonthSelect(monthIndex)}
                disabled={isFutureMonth}
              >
                {label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const filterIcons = {
  is_live: StatusOnlineIcon,
  is_active: StatusOnlineIcon,
  status: BadgeCheckIcon,
  created_before: ClockIcon,
  created_after: ClockIcon,
  plan: PercentageSquareIcon,
  start_date: ClockIcon,
  renewal_date_from: ClockIcon,
  renewal_date_to: ClockIcon,
  amount_less_than: DollarCircleIcon,
  amount_more_than: DollarCircleIcon,
  provider_live_status: StatusOnlineIcon,
  type: MoneySendIcon,
  billable: EmptyWalletTickIcon,
  recovered: OptimizationIcon,
  currency: DollarSquareIcon,
  settlement_currency: DollarSquareIcon,
  settlement_status: DocumentReportIcon,
  operation_source: MoneyReciveIcon,
  is_wallet: WalletIcon,
  operation_type: MoneyReciveIcon,
  service_provider: PaymentProviderIcon,
  template_type: PaymentProviderIcon,
  provider: PaymentProviderIcon,
  custom_fields: CodeCircleIcon,
  response_status_code: CodeCircleIcon,
  intent: DocumentMetaIcon,
  intent_id: DocumentMetaIcon,
  customer: UsersIcon,
  uuid: ChipIcon,
  id: ChipIcon,
  obj_pk: ChipIcon,
  obj_type: HierarchySquare2Icon,
  transaction_uuid: TransactionsIcon,
  operation: MoneyReciveIcon,
  initiation: MoneyReciveIcon,
  subscription: RefreshIcon,
  recurrence: RefreshIcon,
  custom_recurrence: RefreshIcon,
  merchant_reference: DocumentCodeIcon,
  risk_level: SecurityCardIcon,
  ip_address: GlobeAltIcon,
  account: UserCircleIcon,
  account__id: UserCircleIcon,
  accounts: UserCircleIcon,
  organization: LibraryIcon,
  organization__id: LibraryIcon,
  method: CloudIcon,
  payment_method: PayInProviderIcon,
  customer_phone_number: PhoneIcon,
  customer_email: MailIcon,
  customer_name: DocumentTextIcon,
  settlement_file: DocumentTextIcon,
  card_type: CardLookupIcon,
  card_last_4: CardLookupIcon,
  card_expiry_month: CardLookupIcon,
  card_expiry_year: CardLookupIcon,
  card_bin: CardLookupIcon,
  card_brand: CardLookupIcon,
  card_country: GlobeIcon,
  reconciliation_status: StatusOnlineIcon,
  method_id: CardEditIcon,
  connection_id: ProviderLookupIcon,
  anomaly_detected: ErrorIcon,
  flow_id: ChipIcon,
  attempt_number: DocumentMetaIcon,
  recovery_method: RefreshIcon,
};

// ------ Combobox ------
const ComboboxContext = createContext(null);

const Combobox = ({
  children,
  options,
  recentFilters,
  filterFn,
  itemToString = (item) => item?.[1],
  onSelect,
  setIsOpen,
  isMultiple,
  selected,
  inputValue,
  onInputValueChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filterFnRef = useRef(filterFn);
  filterFnRef.current = filterFn;

  const filteredOptions = useMemo(
    () =>
      !searchTerm || !filterFnRef.current
        ? options
        : options?.filter((item) => filterFnRef.current(item, searchTerm)),
    [options, searchTerm]
  );

  const recentOptions = useMemo(() => {
    if (options.length <= 5 || !recentFilters?.length) return null;
    return recentFilters
      .map((filter) => filteredOptions.find(([value]) => value === filter))
      .filter(Boolean);
  }, [options, filteredOptions, recentFilters]);

  const selectedItem = useMemo(() => {
    if (!options?.length) return [];

    if (isMultiple) {
      return (
        selected
          ?.map?.((v) => options.find((option) => option[0] === String(v)))
          ?.filter((v) => v !== undefined) || []
      );
    }

    return options.find((option) => option[0] === String(selected)) || null;
  }, [options, isMultiple, selected]);

  const items = useMemo(
    () =>
      recentOptions ? [...recentOptions, ...filteredOptions] : filteredOptions,
    [recentOptions, filteredOptions]
  );

  const { getInputProps, getMenuProps, getItemProps, highlightedIndex } =
    useCombobox({
      selectedItem,
      isOpen: true,
      items,
      onIsOpenChange: ({ isOpen, type }) => {
        if (type === useCombobox.stateChangeTypes.InputKeyDownEscape)
          setIsOpen?.(!!isOpen);
      },
      inputValue: inputValue ?? searchTerm,
      onInputValueChange: ({ inputValue }) =>
        onInputValueChange
          ? onInputValueChange(inputValue)
          : setSearchTerm(inputValue ?? ""),
      itemToString,
      onSelectedItemChange: ({ selectedItem }) => onSelect(selectedItem),
      stateReducer: isMultiple ? multipleStateReducer : singleStateReducer,
      scrollIntoView,
    });

  return (
    <ComboboxContext.Provider
      value={{
        getInputProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        filteredOptions,
        isMultiple,
        recentOptions,
      }}
    >
      {children}
    </ComboboxContext.Provider>
  );
};

Combobox.Input = function ComboboxInput() {
  const { getInputProps } = useContext(ComboboxContext);
  return (
    <Input
      autoFocus
      icon={<SearchIcon />}
      placeholder="Search filters"
      containerClassName="flex-1"
      nativeChangeEvent
      {...getInputProps()}
    />
  );
};

Combobox.Options = function ComboboxOptions({
  renderOption,
  isSelected,
  showOptions = true,
  children,
}) {
  const {
    filteredOptions,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    isMultiple,
    recentOptions,
  } = useContext(ComboboxContext);

  return (
    <ChoicesList
      choices={filteredOptions}
      recentOptions={recentOptions}
      render={renderOption}
      getItemProps={getItemProps}
      getMenuProps={getMenuProps}
      highlightedIndex={highlightedIndex}
      isSelected={isSelected}
      isMultiple={isMultiple}
      showOptions={showOptions}
    >
      {children}
    </ChoicesList>
  );
};

const multipleStateReducer = (state, actionAndChanges) => {
  const { changes, type } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
      return {
        ...changes,
        inputValue: state.inputValue || "",
      };

    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return {
        ...changes,
        isOpen: true, // keep dropdown open after selection
        highlightedIndex: state.highlightedIndex, // keep highlighted index position
        inputValue: state.inputValue, // keep search query
      };

    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...changes,
        selectedItem: state.selectedItem,
        highlightedIndex: state.highlightedIndex,
        inputValue: state.inputValue,
      };

    case useCombobox.stateChangeTypes.InputClick:
      return {
        ...changes,
        isOpen: true,
      };
    default:
      return changes;
  }
};

const singleStateReducer = (state, actionAndChanges) => {
  const { changes, type } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
      return {
        ...changes,
        inputValue: state.inputValue || "",
      };

    // reset selected item if reselected again
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return {
        ...changes,
        selectedItem:
          state.selectedItem?.[0] === changes.selectedItem?.[0]
            ? null
            : changes.selectedItem,
      };

    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...changes,
        selectedItem: state.selectedItem,
        highlightedIndex: state.highlightedIndex,
        inputValue: state.inputValue,
      };

    case useCombobox.stateChangeTypes.InputClick:
      return {
        ...changes,
        isOpen: true,
      };
    default:
      return changes;
  }
};

const scrollIntoView = (node) => {
  requestAnimationFrame
    ? requestAnimationFrame(() => {
        node.scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
      })
    : setTimeout(() => {
        node.scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
      }, 0);
};
